import { useEffect, useRef, useState } from "react";
import { WATER } from "../lib/nav";

const SEGS = [
  ["People", ["PEOPLE"], ["Investors", "Founders", "Experts"]],
  ["Practitioners", ["PRACTI-", "TIONERS"], ["Doctors", "Healers", "Coaches"]],
  ["Places", ["PLACES"], ["Countries", "Regions"]],
  ["Passages", ["PASS-", "AGES"], ["Rail", "Yachts", "Aviation"]],
  ["Properties", ["PROPER-", "TIES"], ["Hotels", "Resorts", "Clinics"]],
  ["Products", ["PRODUCTS"], ["Supplements", "Wearables", "Skincare"]],
  ["Programmes", ["PROGRAM-", "MES"], ["Retreats", "Courses", "Protocols"]],
];
const ARC = (2 * Math.PI) / 7;
const R1 = 190, R0 = 82, C = 200;
const pt = (r, a) => [C + r * Math.cos(a), C + r * Math.sin(a)];
const FONT = { fontFamily: "'Tenor Sans', sans-serif" };

function useAutoCycle(paused) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.4 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!visible || paused) return undefined;
    const id = setInterval(() => setStep((s) => (s + 1) % SEGS.length), 2000);
    return () => clearInterval(id);
  }, [visible, paused]);
  return [ref, step];
}

export default function SixPWheel({ active, onPick }) {
  const [hover, setHover] = useState(null);
  const [paused, setPaused] = useState(false);
  const [ref, step] = useAutoCycle(paused || hover !== null);
  const lit = active ? SEGS.findIndex(([l]) => l === active) : hover ?? (paused ? -1 : step);
  const tap = (label) => {
    const next = active === label ? null : label;
    setPaused(next !== null);
    onPick(next);
  };
  return (
    <svg ref={ref} viewBox="0 0 400 400" className="w-full max-w-[22rem] mx-auto overflow-visible p-1" data-testid="sixp-wheel" data-cycle={lit}>
      <defs><pattern id="wave" patternUnits="userSpaceOnUse" width="400" height="400"><image href={WATER} width="400" height="400" preserveAspectRatio="xMidYMid slice" /></pattern></defs>
      {SEGS.map(([label, lines, words], i) => {
        const a0 = -Math.PI / 2 + i * ARC, a1 = a0 + ARC, am = (a0 + a1) / 2;
        const [x0, y0] = pt(R1, a0), [x1, y1] = pt(R1, a1), [x2, y2] = pt(R0, a1), [x3, y3] = pt(R0, a0);
        const on = lit === i;
        const [tx, ty] = pt((R0 + R1) / 2 + 4, am);
        const rows = on ? [...lines, ...words] : lines;
        const top = ty - ((rows.length - 1) * 11) / 2;
        return (
          <g key={label} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onClick={() => tap(label)}
            className="cursor-pointer" data-testid={`sixp-seg-${label.toLowerCase()}`}>
            <path d={`M${x0},${y0} A${R1},${R1} 0 0 1 ${x1},${y1} L${x2},${y2} A${R0},${R0} 0 0 0 ${x3},${y3} Z`}
              fill={on ? "#111" : "#F7F3EA"} stroke="#C9A25C" strokeWidth="1" style={{ transition: "fill 0.6s" }} />
            {rows.map((r, k) => (
              <text key={r} x={tx} y={top + k * 11} textAnchor="middle" dominantBaseline="middle" style={FONT}
                fontSize={k < lines.length ? 8.5 : 6.5} letterSpacing={k < lines.length ? 1.3 : 0.5}
                fill={on ? "#C9A25C" : "#2A2A2A"}>{k < lines.length ? r : r.toUpperCase()}</text>
            ))}
          </g>
        );
      })}
      <circle cx={C} cy={C} r={R1 + 6} fill="none" stroke="url(#wave)" strokeWidth="10" />
      <circle cx={C} cy={C} r={R0 - 3} fill="#0d0d0d" stroke="#C9A25C" strokeWidth="0.8" data-testid="sixp-centre" />
      <text x={C} y={C - 6} textAnchor="middle" fontSize="15" letterSpacing="2.5" fill="#F7E7B8" style={FONT}>LEVERAGE</text>
      <text x={C} y={C + 14} textAnchor="middle" fontSize="15" letterSpacing="2.5" fill="#F7E7B8" style={FONT}>LINKS</text>
    </svg>
  );
}
